/**
 * "Documenting the Consent Process" — the NIDCR v2.0 source-document sheet,
 * captured on Step 2 before any consent session starts (all three methods).
 *
 * Two halves:
 *   - the coordinator's attestations (language, interpreter, questions, copy,
 *     timing, witness, risks, notes) — answered here;
 *   - the system's evidence about the staff co-signer (closed trainings and
 *     active DoA delegation on this study) — read-only, fetched for the
 *     selected co-signer and frozen onto the consent record by the server.
 */
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { consent, type ConsentProcessDocumentation } from '@/api/client';

export type ProcessDocDraft = Partial<ConsentProcessDocumentation>;

export const EMPTY_PROCESS_DOC: ProcessDocDraft = { cf_language: 'english' };

/** Same rules the server enforces (assertProcessDocumentationComplete). */
export function validateProcessDoc(d: ProcessDocDraft): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.subject_primary_language?.trim()) e.subject_primary_language = "Enter the subject's primary language.";
  if (!d.cf_language) e.cf_language = 'Select the language of the consent form reviewed.';
  if (d.cf_language === 'other' && !d.cf_language_other?.trim()) e.cf_language_other = 'Name the language.';
  if (d.cf_language && d.cf_language !== 'english' && d.interpreter_available === undefined) {
    e.interpreter_available = 'State whether an interpreter was available.';
  }
  for (const key of ['time_for_questions', 'copy_provided', 'signed_before_procedures', 'witness_present', 'risks_reviewed'] as const) {
    if (d[key] === undefined) e[key] = 'Required.';
  }
  if (d.time_for_questions === false && !d.time_for_questions_reason?.trim()) e.time_for_questions_reason = 'Please explain.';
  if (d.copy_provided === false && !d.copy_provided_reason?.trim()) e.copy_provided_reason = 'Please explain.';
  if (d.signed_before_procedures === false && !d.signed_before_procedures_reason?.trim()) {
    e.signed_before_procedures_reason = 'Please explain.';
  }
  if (d.witness_present && !d.witness_name?.trim()) e.witness_name = 'Name the witness.';
  return e;
}

export function toProcessDocPayload(d: ProcessDocDraft): ConsentProcessDocumentation {
  return {
    subject_primary_language: d.subject_primary_language?.trim() ?? '',
    cf_language: d.cf_language ?? 'english',
    cf_language_other: d.cf_language === 'other' ? d.cf_language_other?.trim() : undefined,
    interpreter_available: d.cf_language === 'english' ? undefined : d.interpreter_available,
    interpreter_name: d.cf_language === 'english' ? undefined : d.interpreter_name?.trim() || undefined,
    time_for_questions: !!d.time_for_questions,
    time_for_questions_reason: d.time_for_questions ? undefined : d.time_for_questions_reason?.trim(),
    copy_provided: !!d.copy_provided,
    copy_provided_reason: d.copy_provided ? undefined : d.copy_provided_reason?.trim(),
    signed_before_procedures: !!d.signed_before_procedures,
    signed_before_procedures_reason: d.signed_before_procedures ? undefined : d.signed_before_procedures_reason?.trim(),
    witness_present: !!d.witness_present,
    witness_name: d.witness_present ? d.witness_name?.trim() : undefined,
    risks_reviewed: !!d.risks_reviewed,
    additional_notes: d.additional_notes?.trim() || undefined,
  };
}

const INPUT = 'mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm';
const ERR = 'mt-1 text-[11px] text-red-600';

function YesNo({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string;
  value: boolean | undefined;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-1 flex gap-4 text-sm">
      {[true, false].map((v) => (
        <label key={String(v)} className="flex items-center gap-1.5">
          <input
            type="radio"
            name={name}
            checked={value === v}
            disabled={disabled}
            onChange={() => onChange(v)}
          />
          {v ? 'Yes' : 'No'}
        </label>
      ))}
    </div>
  );
}

export function ConsentProcessDocumentationFields({
  value,
  onChange,
  errors,
  subjectId,
  staffUserId,
  consentMode,
  disabled,
}: {
  value: ProcessDocDraft;
  onChange: (next: ProcessDocDraft) => void;
  errors: Record<string, string>;
  subjectId: string;
  staffUserId: string;
  consentMode: 'in_person' | 'in_person_esign' | 'remote';
  disabled?: boolean;
}) {
  const set = <K extends keyof ProcessDocDraft>(k: K, v: ProcessDocDraft[K]) => onChange({ ...value, [k]: v });

  const evidenceQ = useQuery({
    queryKey: ['consent', 'process-evidence', subjectId, staffUserId],
    queryFn: () => consent.processEvidence(subjectId, staffUserId),
    enabled: !!subjectId && !!staffUserId,
    staleTime: 60_000,
  });
  const ev = evidenceQ.data;

  return (
    <fieldset className="space-y-3" data-testid="consent-process-documentation">
      <legend className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
        <ClipboardCheck className="h-4 w-4 text-accent-700" />
        Consent process documentation
      </legend>
      <p className="text-xs text-ink-muted">
        Source record of the consent discussion (NIDCR &ldquo;Documenting the Consent Process&rdquo;, v2.0).
        Required before the session can start; saved with the consent record and printed on the certificate.
      </p>

      {/* System evidence about the staff co-signer */}
      <div className="rounded border border-surface-border bg-surface-muted/30 px-3 py-2 text-xs">
        <div className="font-medium text-ink-secondary">Staff member conducting the consent discussion</div>
        {!staffUserId ? (
          <div className="mt-1 text-ink-muted">Select the staff co-signer above to check their training and delegation.</div>
        ) : evidenceQ.isLoading ? (
          <div className="mt-1 flex items-center gap-2 text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking training and delegation…
          </div>
        ) : evidenceQ.isError || !ev ? (
          <div className="mt-1 text-amber-700">Could not read training / delegation evidence — it will be re-checked when the session starts.</div>
        ) : (
          <dl className="mt-1 grid gap-x-4 gap-y-1 sm:grid-cols-2">
            <div>
              <dt className="text-ink-muted">Qualified / trained (system evidence)</dt>
              <dd className={ev.trained ? 'text-emerald-700' : 'text-red-700'}>
                {ev.trained ? 'Yes' : 'No closed training on this study'}
                {ev.trainings.length > 0 && (
                  <span className="block text-[11px] text-ink-muted">
                    {ev.trainings.map((t) => `${t.reference_number} · ${t.document_type_name}`).join(', ')}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-ink-muted">Delegated on the DoA (system evidence)</dt>
              <dd className={ev.delegated ? 'text-emerald-700' : 'text-red-700'}>
                {ev.delegated ? (ev.consent_responsibility ? 'Yes — includes consent' : 'Yes') : 'No active delegation'}
                {ev.delegation?.responsibilities.length ? (
                  <span className="block text-[11px] text-ink-muted">{ev.delegation.responsibilities.join(', ')}</span>
                ) : null}
              </dd>
            </div>
            {ev.evidence_errors?.length ? (
              <div className="sm:col-span-2 text-amber-700">Some lookups failed: {ev.evidence_errors.join('; ')}</div>
            ) : null}
          </dl>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">
            Subject&rsquo;s primary language <span className="text-red-600">*</span>
          </span>
          <input
            type="text"
            className={INPUT}
            value={value.subject_primary_language ?? ''}
            disabled={disabled}
            onChange={(e) => set('subject_primary_language', e.target.value)}
          />
          {errors.subject_primary_language && <div className={ERR}>{errors.subject_primary_language}</div>}
        </label>
        <div className="block text-xs">
          <span className="font-medium text-ink-secondary">
            Language of consent form reviewed <span className="text-red-600">*</span>
          </span>
          <div className="mt-1 flex flex-wrap gap-4 text-sm">
            {(['english', 'spanish', 'other'] as const).map((l) => (
              <label key={l} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="cf_language"
                  checked={value.cf_language === l}
                  disabled={disabled}
                  onChange={() => set('cf_language', l)}
                />
                {l === 'english' ? 'English' : l === 'spanish' ? 'Spanish' : 'Other'}
              </label>
            ))}
          </div>
          {value.cf_language === 'other' && (
            <input
              type="text"
              className={INPUT}
              placeholder="Specify language"
              value={value.cf_language_other ?? ''}
              disabled={disabled}
              onChange={(e) => set('cf_language_other', e.target.value)}
            />
          )}
          {errors.cf_language && <div className={ERR}>{errors.cf_language}</div>}
          {errors.cf_language_other && <div className={ERR}>{errors.cf_language_other}</div>}
        </div>
      </div>

      {value.cf_language && value.cf_language !== 'english' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="block text-xs">
            <span className="font-medium text-ink-secondary">
              Was an interpreter available? <span className="text-red-600">*</span>
            </span>
            <YesNo
              name="interpreter_available"
              value={value.interpreter_available}
              disabled={disabled}
              onChange={(v) => set('interpreter_available', v)}
            />
            {errors.interpreter_available && <div className={ERR}>{errors.interpreter_available}</div>}
          </div>
          {value.interpreter_available && (
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Interpreter name</span>
              <input
                type="text"
                className={INPUT}
                value={value.interpreter_name ?? ''}
                disabled={disabled}
                onChange={(e) => set('interpreter_name', e.target.value)}
              />
            </label>
          )}
        </div>
      )}

      <div className="block text-xs">
        <span className="font-medium text-ink-secondary">
          Were the risks associated with the study reviewed with the participant? <span className="text-red-600">*</span>
        </span>
        <YesNo name="risks_reviewed" value={value.risks_reviewed} disabled={disabled} onChange={(v) => set('risks_reviewed', v)} />
        {errors.risks_reviewed && <div className={ERR}>{errors.risks_reviewed}</div>}
      </div>

      {(
        [
          ['time_for_questions', 'Was time allowed to ask / answer questions?', 'time_for_questions_reason'],
          [
            'copy_provided',
            consentMode === 'remote'
              ? 'Will a copy of the signed consent be provided to the subject? (remote e-sign emails the signed PDF automatically)'
              : 'Was / will a copy of the signed consent form be provided to the subject?',
            'copy_provided_reason',
          ],
          ['signed_before_procedures', 'Is the consent form being signed prior to initiation of study procedures?', 'signed_before_procedures_reason'],
        ] as const
      ).map(([key, label, reasonKey]) => (
        <div key={key} className="block text-xs">
          <span className="font-medium text-ink-secondary">
            {label} <span className="text-red-600">*</span>
          </span>
          <YesNo name={key} value={value[key]} disabled={disabled} onChange={(v) => set(key, v)} />
          {errors[key] && <div className={ERR}>{errors[key]}</div>}
          {value[key] === false && (
            <>
              <input
                type="text"
                className={INPUT}
                placeholder="If not, please explain"
                value={value[reasonKey] ?? ''}
                disabled={disabled}
                onChange={(e) => set(reasonKey, e.target.value)}
              />
              {errors[reasonKey] && <div className={ERR}>{errors[reasonKey]}</div>}
            </>
          )}
        </div>
      ))}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="block text-xs">
          <span className="font-medium text-ink-secondary">
            Was a witness present for the consent discussion? <span className="text-red-600">*</span>
          </span>
          <YesNo name="witness_present" value={value.witness_present} disabled={disabled} onChange={(v) => set('witness_present', v)} />
          {errors.witness_present && <div className={ERR}>{errors.witness_present}</div>}
        </div>
        {value.witness_present && (
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">
              Witness name <span className="text-red-600">*</span>
            </span>
            <input
              type="text"
              className={INPUT}
              value={value.witness_name ?? ''}
              disabled={disabled}
              onChange={(e) => set('witness_name', e.target.value)}
            />
            {errors.witness_name && <div className={ERR}>{errors.witness_name}</div>}
          </label>
        )}
      </div>

      <label className="block text-xs">
        <span className="font-medium text-ink-secondary">Additional notes</span>
        <textarea
          className={`${INPUT} min-h-[64px]`}
          placeholder="e.g. special circumstances — literacy, translation, questions raised"
          value={value.additional_notes ?? ''}
          disabled={disabled}
          maxLength={2000}
          onChange={(e) => set('additional_notes', e.target.value)}
        />
      </label>
    </fieldset>
  );
}
